import { useStore } from '../store/useStore';
import { MODELS_DATABASE } from '../config/modelsDatabase';

interface ModelSwitchResult {
  shouldSwitch: boolean;
  targetModel: string;
  reason: string;
}

export function checkImageModelSwitch(currentModelId: string | undefined): ModelSwitchResult {
  const language = localStorage.getItem('language') || 'en';
  
  // Find Grok 2 and OpenAI vision models
  const visionModels = Object.values(MODELS_DATABASE).filter(
    model => (model.provider === 'xai' || model.provider === 'openai') && model.capabilities?.vision
  );

  const grokModel = { id: 'grok-2-vision-latest', name: 'Grok 2 Vision' };
  const openaiModels = visionModels.filter(model => model.provider === 'openai');

  if (!grokModel && openaiModels.length === 0) {
    return {
      shouldSwitch: false,
      targetModel: currentModelId || '',
      reason: language === 'es'
        ? 'No se encontró un modelo compatible con imágenes'
        : 'No image-compatible model found'
    };
  }

  // If no model selected or current model is not vision-capable
  if (!currentModelId || !MODELS_DATABASE[currentModelId]?.capabilities?.vision) {
    const openaiModelNames = openaiModels.map(m => m.name).join(', ');
    return {
      shouldSwitch: true,
      targetModel: grokModel?.id || openaiModels[0].id,
      reason: language === 'es'
        ? `Para procesar imágenes, necesitamos cambiar a un modelo con capacidades de visión. Sugerimos ${grokModel ? 'Grok 2 Vision' : ''} ${openaiModels.length > 0 ? 'o ' + openaiModelNames : ''}. ¿Desea continuar?`
        : `To process images, we need to switch to a vision-capable model. We suggest ${grokModel ? 'Grok 2 Vision' : ''} ${openaiModels.length > 0 ? 'or ' + openaiModelNames : ''}. Would you like to proceed?`
    };
  }

  return {
    shouldSwitch: false,
    targetModel: currentModelId,
    reason: ''
  };
}

export async function confirmModelSwitch(result: ModelSwitchResult): Promise<boolean> {
  if (!result.shouldSwitch) return true;
  
  return new Promise((resolve) => {
    const confirmed = window.confirm(result.reason);
    if (confirmed) {
      const baseModel = MODELS_DATABASE[result.targetModel];
      if (baseModel) {
        const { apiConfigs } = useStore.getState();
        const aiModel = {
          ...baseModel,
          isConfigured: apiConfigs.some(config => config.provider === baseModel.provider),
          systemApiCost: baseModel.pricing.prompt_tokens_per_1k * 1000 // Convert to per 1M tokens
        };
        useStore.getState().selectModel(aiModel);
      }
    }
    resolve(confirmed);
  });
}